import { lazy, Suspense, useState } from "react";
import {
  Bell,
  BellOff,
  BellRing,
  Check,
  ChevronDown,
  Bot,
  HelpCircle,
  PenLine,
  Loader2,
  Menu,
  Monitor,
  Moon,
  Search,
  Settings,
  Smartphone,
  Sparkles,
  Sun,
  type LucideIcon,
} from "lucide-react";
import { useIsMutating } from "@tanstack/react-query";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { BACKGROUND_SYNC_MUTATION_KEY } from "@/hooks/useBackgroundSync";
import { useLeadsHeute } from "@/hooks/useLeadsHeute";
import { useNotifications } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCommandPalette } from "@/components/domain/CommandPaletteProvider";
import {
  FeedbackButton,
  FeedbackMenuButton,
  useFeedbackFlow,
} from "@/components/feedback/FeedbackButton";
import { HomeLogoLink } from "@/components/shell/HomeLogoLink";
import { ICONS_BY_GROUP_ID, ICONS_BY_NAV_ID } from "@/components/shell/navIcons";
import { StandaloneBackButton } from "@/components/shell/StandaloneBackButton";

// G-13: die Push-Kategorie-UI liegt in einem LAZY-Chunk (raus aus dem
// app-*.js-Initial-Bundle, size-limit-Budget). Desktop-Dialog wird erst beim
// Oeffnen gemountet, die Mobile-Inline-Controls erst wenn die Sheet offen ist.
const PushPrefsDialog = lazy(() => import("@/components/shell/PushPrefsDialog"));
// Ebenso lazy: das Initial-Bundle traegt nur den Menueeintrag, nicht den
// Dialog samt Datei-Auswahl (Bundle-Deckel, size-limit-Budget).
const SignatureDialog = lazy(
  () => import("@/components/shell/SignatureDialog"),
);
const PushCategoryControls = lazy(() =>
  import("@/components/shell/PushCategoryControls").then((m) => ({
    default: m.PushCategoryControls,
  })),
);
import { useIsNavWide } from "@/hooks/useBreakpoint";
import { useAuth, useIsSuperadmin } from "@/lib/auth/AuthContext";
import { leadsHeuteZahl } from "@shared/leads/leadsHeute";
import { ohneLeadsMenueKarten } from "@shared/leads/leadsMenue";
import {
  topLevelNavForRole,
  USER_ROLE_LABELS,
  type NavGroupDefinition,
  type NavItemDefinition,
  type TopLevelNavEntry,
} from "@/lib/auth/roles";
import { SperrGrund } from "@/components/ui/sperr-grund";
import { useDesktopNotificationToggle } from "@/lib/notifications/desktopNotifications";
import { setTheme, useTheme, type ThemeChoice } from "@/lib/theme";
import { cn } from "@/lib/utils";

// Aktiv, wenn die aktuelle Route das Ziel IST oder darunter liegt (Detail-
// Seiten). `end`-Sonderfall braucht es hier nicht — Gruppen sammeln nie Home.
function isNavPathActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

const THEME_OPTIONS: readonly {
  value: ThemeChoice;
  label: string;
  icon: LucideIcon;
}[] = [
  { value: "light", label: "Hell", icon: Sun },
  { value: "dark", label: "Dunkel", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

export function TopNav() {
  const { user } = useAuth();
  const isSuperadmin = useIsSuperadmin();
  // POL-2: entkoppelter Nav-Breakpoint (1280 px). Unter der Schwelle traegt
  // der Sheet-Drawer die Nav — die volle Leiste passt sonst nicht (iPad quer /
  // schmales Fenster). Der Kalkulations-Breakpoint (768 px) bleibt unberuehrt.
  const isNavWide = useIsNavWide();

  // NAV-1: EINE Leiste pro Rolle aus dem einen Geruest — Desktop-Leiste und
  // Mobile-Sheet rendern dieselbe Struktur (Blaetter + zugeklappte Gruppen).
  const entries: readonly TopLevelNavEntry[] = user
    ? topLevelNavForRole(user.role)
    : [];

  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b border-border bg-background/85 px-4 backdrop-blur"
      data-testid="top-nav"
    >
      {isNavWide ? (
        <DesktopTopNav entries={entries} isSuperadmin={isSuperadmin} />
      ) : (
        <MobileTopNav entries={entries} isSuperadmin={isSuperadmin} />
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Desktop-Leiste (≥ 1280 px, POL-2). Darunter uebernimmt die Mobile-Shell den
// Sheet-Drawer. Die Faltung der Gruppen entscheidet `buildTopLevelNav` ueber
// die Leistenlaenge der Rolle (NAV-1): Sandro sieht 4 direkte Reiter + 2
// Sammel-Dropdowns, Marvin und Jacqueline ihre 5 bzw. 6 Reiter flach.
// ---------------------------------------------------------------------------

function DesktopTopNav({
  entries,
  isSuperadmin,
}: {
  readonly entries: readonly TopLevelNavEntry[];
  readonly isSuperadmin: boolean;
}) {
  const palette = useCommandPalette();

  return (
    <>
      {/* NAV-1, gemessen bei 1280 px: die Leiste (links) darf nie schrumpfen oder
          unter das Suchfeld rutschen — der rechte Block gibt nach (Suchfeld
          schmaler bis 2xl, Konto-Name kuerzt sich per Ellipse). Vorher lag
          Jacquelines «Produkte» bei 1280 px mit Meldungs-Badge unter dem
          Suchfeld (31 px Ueberlappung, Bestand vor NAV-1). */}
      <div className="flex shrink-0 items-center gap-6">
        {/* NAV-5: der Zurueck-Pfeil nur in der installierten App (sonst null).
            Er kostet 36 px; die Leiste bleibt `shrink-0`, der Konto-Name
            rechts gibt per Ellipse nach — gemessen in nav-zurueck-pfeil.spec. */}
        <div className="flex items-center gap-1">
          <StandaloneBackButton className="size-8" />
          <HomeLogoLink />
        </div>
        <nav
          className="flex items-center gap-1"
          aria-label="Hauptnavigation"
          data-testid="topnav-bar"
        >
          {entries.map((entry) =>
            entry.kind === "leaf" ? (
              <DesktopNavLeaf key={entry.item.id} item={entry.item} />
            ) : (
              <DesktopNavGroup
                key={entry.group.id}
                group={entry.group}
                items={entry.items}
              />
            ),
          )}
        </nav>
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={palette.toggle}
          className="inline-flex h-8 min-w-48 shrink-0 items-center gap-3 rounded-sm border border-border bg-surface px-3 text-xs text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground 2xl:min-w-60"
        >
          <span>Suchen oder Aktion …</span>
          <span className="ml-auto font-mono text-2xs tracking-widest">⌘K</span>
        </button>

        <SyncSpinner />
        <FeedbackButton />
        <NotificationBadgeButton />

        <DesktopAccountMenu isSuperadmin={isSuperadmin} />
      </div>
    </>
  );
}

// Direkter Reiter der Desktop-Leiste (unveraendertes Verhalten aus der Zeit vor
// POL-2 — nur in eine eigene Komponente ausgelagert, weil neben ihm jetzt auch
// Sammel-Dropdowns stehen).
function DesktopNavLeaf({ item }: { readonly item: NavItemDefinition }) {
  const Icon = ICONS_BY_NAV_ID[item.id];
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "inline-flex h-8 items-center gap-2 rounded-sm px-3 text-xs font-medium transition-colors",
          item.disabled && "pointer-events-none text-muted-foreground/60",
          !item.disabled &&
            !isActive &&
            "text-muted-foreground hover:bg-surface hover:text-foreground",
          !item.disabled && isActive && "bg-surface text-foreground",
        )
      }
      aria-disabled={item.disabled || undefined}
      tabIndex={item.disabled ? -1 : 0}
    >
      <Icon className="size-3.5" />
      {item.label}
      {item.id === "leads" ? <LeadsNavZahl /> : null}
    </NavLink>
  );
}

// Die Zahl am Menuepunkt «Leads» (LEAD-25): was HEUTE auf mich wartet —
// dieselbe Uebersicht wie die Seite, in meiner Standard-Sicht; beim
// Koordinator zaehlt «Verteilen» mit. Ohne Wartendes steht nichts da.
function LeadsNavZahl() {
  const heute = useLeadsHeute();
  const zahl = heute.uebersicht ? leadsHeuteZahl(heute.uebersicht, heute.standardSicht) : 0;
  if (zahl === 0) return null;
  return (
    <span
      // Bewusst NICHT rot (FB-15: Rot heisst «hier handelst du») und nicht
      // die Alarmfarbe der Glocke — eine ruhige, klar lesbare Zahl.
      className="inline-flex min-h-4 min-w-[1.1rem] items-center justify-center rounded-full bg-foreground px-1 font-mono text-2xs font-semibold leading-none text-background"
      data-testid="topnav-leads-count"
      aria-label={`${String(zahl)} für heute`}
    >
      {zahl > 99 ? "99+" : String(zahl)}
    </span>
  );
}

// Sammel-Dropdown der Admin-Leiste (POL-2): «Finanzen» / «Verwaltung». Der
// Trigger traegt Gruppen-Icon + Label + Chevron und wird aktiv markiert, sobald
// die aktuelle Route unter einem seiner Ziele liegt — so weiss der Nutzer, dass
// er sich «unter Finanzen» befindet, obwohl der Reiter zugeklappt ist.
function DesktopNavGroup({
  group,
  items,
}: {
  readonly group: NavGroupDefinition;
  readonly items: readonly NavItemDefinition[];
}) {
  const location = useLocation();
  const GroupIcon = ICONS_BY_GROUP_ID[group.id];
  const groupActive = items.some((item) =>
    isNavPathActive(location.pathname, item.to),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`topnav-group-${group.id}`}
          data-active={groupActive || undefined}
          className={cn(
            "inline-flex h-8 items-center gap-2 rounded-sm px-3 text-xs font-medium transition-colors",
            groupActive
              ? "bg-surface text-foreground"
              : "text-muted-foreground hover:bg-surface hover:text-foreground",
          )}
        >
          <GroupIcon className="size-3.5" />
          {group.label}
          <ChevronDown className="size-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((item) => {
          const Icon = ICONS_BY_NAV_ID[item.id];
          const active = isNavPathActive(location.pathname, item.to);
          return (
            <DropdownMenuItem
              key={item.id}
              asChild
              disabled={item.disabled}
              data-testid={`topnav-group-item-${item.id}`}
            >
              <NavLink to={item.to} end={item.end}>
                <Icon className="size-3.5 text-muted-foreground" />
                <span>{item.label}</span>
                {active ? (
                  <Check className="ml-auto size-3.5 text-foreground" />
                ) : null}
              </NavLink>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Sichtbarer Indikator fuer einen laufenden Background-Sync — gespeist aus
// dem TanStack-Query-Mutation-State des `useBackgroundSync`-Hooks. Nur sichtbar
// solange wenigstens eine Mutation aktiv ist.
function SyncSpinner() {
  const count = useIsMutating({
    mutationKey: [...BACKGROUND_SYNC_MUTATION_KEY],
  });
  if (count === 0) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex size-8 items-center justify-center text-muted-foreground"
          data-testid="topnav-sync-spinner"
          // role="status" traegt implizit aria-live=polite und erlaubt das
          // aria-label — auf einem rollenlosen <span> ist es verboten (axe
          // aria-prohibited-attr; flackerte in a11y-Specs, weil der Spinner
          // nur waehrend laufender Sync-Mutationen mountet).
          role="status"
          aria-label="bexio-Sync läuft"
        >
          <Loader2 className="size-3.5 animate-spin" />
        </span>
      </TooltipTrigger>
      <TooltipContent>bexio-Sync läuft</TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Phase 5.37 — TopNav-Bell-Badge mit Count (Honest-Pushback-Beigabe).
// ---------------------------------------------------------------------------
// Notifications wandern in 5.37 ans Ende der Home-Page. Der Badge in der
// TopNav haelt die Awareness ohne Scrollen — Sandro sieht "9 offen", clickt,
// landet im NotificationCenter (scrollt auf #notifications). Renderloesung
// rein clientseitig: button + scrollIntoView. Bei pathname !== Ziel navigieren
// wir vorher dorthin und scrollen nach naechstem Tick. KDO-2: der Superadmin
// hat seinen Feed nicht mehr auf der Startseite, sondern unter /system.
function NotificationBadgeButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperadmin = useIsSuperadmin();
  const { user } = useAuth();
  const { data } = useNotifications();
  // LEAD-23: die Glocke zaehlt, was auf der Startseite steht. Karten zu
  // Anfragen stehen fuer PL und Geschaeftsfuehrung im Menue «Leads» und
  // tragen dort ihre eigene Zahl — hier doppelt gezaehlt, fuehrte der Klick
  // auf eine Liste, die kuerzer ist als die Zahl.
  const count = ohneLeadsMenueKarten(data?.items ?? [], user?.role).length;
  if (count === 0) return null;
  const ziel = isSuperadmin ? "/system" : "/";
  const handleClick = () => {
    if (location.pathname !== ziel) {
      navigate(ziel);
      // Naechster Tick: das DOM rendert die Home, dann scrollen wir.
      setTimeout(() => {
        document
          .getElementById("notifications")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    document
      .getElementById("notifications")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const label = `Benachrichtigungen (${String(count)} offen)`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          aria-label={label}
          data-testid="topnav-notification-badge"
          data-count={String(count)}
          className="relative inline-flex size-8 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
        >
          <Bell className="size-4" />
          <span
            className="absolute -right-1 -top-1 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-destructive px-1 font-mono text-2xs font-semibold leading-none text-destructive-foreground"
            data-testid="topnav-notification-badge-count"
          >
            {count > 99 ? "99+" : String(count)}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// Das Konto-Menue ist seit NAV-1 persoenlich: Darstellung, Benachrichtigungen,
// die eigene Unterschrift, Hilfe, Abmelden. Die Firmen-Dinge (Einstellungen,
// Partner Portal, Aktivitaeten) stehen in der Leiste unter «Verwaltung» —
// hier lag vorher ein zweiter Verwaltungs-Block, und «Einstellungen» neben
// «System-Einstellungen» klang gleich und meinte Verschiedenes. Einzig der
// Superadmin behaelt seinen Weg zum Kommandostand («System»), weil er sonst
// keine Leiste hat (POL-1, Home-only).
export function DesktopAccountMenu({
  isSuperadmin,
  defaultOpen = false,
}: {
  readonly isSuperadmin: boolean;
  // `defaultOpen` ist nur fuer Unit-Tests gedacht: Radix' Trigger-Pointer-
  // Events lassen sich in jsdom nicht zuverlaessig dispatchen, daher
  // rendert der Test das Menue direkt geoeffnet. Default `false` ist
  // noetig wegen `exactOptionalPropertyTypes` — sonst landet `undefined`
  // im Radix-Prop, das nur `boolean` akzeptiert.
  readonly defaultOpen?: boolean;
}) {
  const { user, signOut } = useAuth();
  const theme = useTheme();
  // G-13: Dialog-State liegt HIER (nicht im DropdownMenuContent) — die
  // DesktopAccountMenu-Komponente bleibt gemountet, wenn das Menue schliesst.
  const [pushPrefsOpen, setPushPrefsOpen] = useState(false);
  // Marvin 2026-08-11: die eigene Unterschrift fuers Angebot. Wie die
  // Push-Einstellungen eine reine Per-Mensch-Sache — kein Rollen-Gate, und
  // der Zustand liegt hier, damit der Dialog das Schliessen des Menues
  // ueberlebt.
  const [signatureOpen, setSignatureOpen] = useState(false);

  return (
    <>
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="min-w-0 max-w-56 gap-2 font-mono text-2xs uppercase tracking-widest"
          aria-label="Konto-Menü"
        >
          {/* truncate + max-w: ein langer Name darf die ≥1100-Leiste nicht
              ueber die Breite druecken (POL-2 Robustheit). */}
          <span className="truncate">
            {user?.fullName} · {user ? USER_ROLE_LABELS[user.role] : ""}
          </span>
          <ChevronDown className="size-3 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Konto</DropdownMenuLabel>
        <DropdownMenuItem disabled>
          <span className="font-mono text-2xs">{user?.email}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Darstellung</DropdownMenuLabel>
        {THEME_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isActive = theme === option.value;
          return (
            <DropdownMenuItem
              key={option.value}
              role="menuitemradio"
              aria-checked={isActive}
              data-theme-choice={option.value}
              onSelect={(event) => {
                event.preventDefault();
                setTheme(option.value);
              }}
            >
              <Icon className="size-3.5 text-muted-foreground" />
              <span>{option.label}</span>
              {isActive ? (
                <Check className="ml-auto size-3.5 text-foreground" />
              ) : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
        <DesktopNotificationMenuItems
          onOpenPushPrefs={() => setPushPrefsOpen(true)}
        />
        {/* Marvin 2026-08-11: «Unterschrift sollte auf dem Angebot automatisch
            erscheinen». Fuer ALLE Rollen — jeder, der eine Offerte macht,
            unterschreibt sie auch. Bewusst NICHT unter «Verwaltung»: es ist
            keine Verwaltung fremder Leute, sondern die eigene Handschrift. */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="account-menu-signature"
          onSelect={() => setTimeout(() => setSignatureOpen(true), 0)}
        >
          <PenLine className="size-3.5 text-muted-foreground" />
          <span>Meine Unterschrift</span>
        </DropdownMenuItem>
        {/* MCP-1: der persoenliche Assistent (Grok Bot) — eine Per-Mensch-
            Sache wie die Unterschrift, fuer alle Rollen. */}
        <DropdownMenuItem asChild data-testid="account-menu-assistent">
          <NavLink to="/assistent">
            <Bot className="size-3.5 text-muted-foreground" />
            <span>Persönlicher Assistent</span>
          </NavLink>
        </DropdownMenuItem>
        {/* H-4: Hilfe fuer alle Rollen (kein Guard). */}
        <DropdownMenuItem asChild data-testid="account-menu-help">
          <NavLink to="/hilfe">
            <HelpCircle className="size-3.5 text-muted-foreground" />
            <span>Hilfe</span>
          </NavLink>
        </DropdownMenuItem>
        {/* NEU-1: die volle Liste hinter dem Fenster «Was ist neu» — die
            Zeilen der letzten 60 Tage fuer die eigene Rolle. */}
        <DropdownMenuItem asChild data-testid="account-menu-whats-new">
          <NavLink to="/was-ist-neu">
            <Sparkles className="size-3.5 text-muted-foreground" />
            <span>Was ist neu</span>
          </NavLink>
        </DropdownMenuItem>
        {isSuperadmin ? (
          <>
            <DropdownMenuSeparator />
            {/* Der Kommandostand des Superadmins. Hiess bis NAV-1 «System-
                Einstellungen» und stand direkt neben «Einstellungen» (Sandros
                Firmen-Einstellungen) — zwei gleich klingende Namen fuer
                Verschiedenes. Der Mobile-Drawer sagte schon immer «System». */}
            <DropdownMenuItem asChild data-testid="account-menu-system">
              <NavLink to="/system">
                <Settings className="size-3.5 text-muted-foreground" />
                <span>System</span>
              </NavLink>
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void signOut();
          }}
        >
          Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {pushPrefsOpen ? (
      <Suspense fallback={null}>
        <PushPrefsDialog open onOpenChange={setPushPrefsOpen} />
      </Suspense>
    ) : null}
    {signatureOpen ? (
      <Suspense fallback={null}>
        <SignatureDialog open onOpenChange={setSignatureOpen} />
      </Suspense>
    ) : null}
    </>
  );
}

// Phase N / NB-1 — Desktop-Benachrichtigungs-Schalter im Konto-Menue. Liegt
// neben „Darstellung", weil beides eine reine Per-User-Praeferenz ist (alle
// Rollen, nicht nur Admin). Ein Klick auf den Schalter ist die User-Geste, die
// `Notification.requestPermission()` braucht.
function DesktopNotificationMenuItems({
  onOpenPushPrefs,
}: {
  readonly onOpenPushPrefs: () => void;
}) {
  const { supported, permission, active, toggle, sendTest } =
    useDesktopNotificationToggle();

  if (!supported) {
    return (
      <DropdownMenuItem disabled>
        <BellOff className="size-3.5 text-muted-foreground" />
        <span>Vom Browser nicht unterstützt</span>
      </DropdownMenuItem>
    );
  }

  const denied = permission === "denied";
  return (
    <>
      <DropdownMenuItem
        role="menuitemcheckbox"
        aria-checked={active}
        disabled={denied}
        data-testid="account-menu-desktop-notifications"
        onSelect={(event) => {
          event.preventDefault();
          void toggle();
        }}
      >
        {active ? (
          <BellRing className="size-3.5 text-muted-foreground" />
        ) : (
          <BellOff className="size-3.5 text-muted-foreground" />
        )}
        <span>Desktop-Benachrichtigungen</span>
        {active ? <Check className="ml-auto size-3.5 text-foreground" /> : null}
      </DropdownMenuItem>
      {denied ? (
        <DropdownMenuItem disabled>
          <span className="text-2xs text-muted-foreground">
            Im Browser blockiert — dort wieder erlauben.
          </span>
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem
        disabled={!active}
        data-testid="account-menu-desktop-notifications-test"
        onSelect={(event) => {
          event.preventDefault();
          void sendTest();
        }}
      >
        <Bell className="size-3.5 text-muted-foreground" />
        {/* UX-7: der Test geht nur bei eingeschaltetem Schalter darüber. */}
        <span className="flex flex-col">
          <span>Test-Benachrichtigung</span>
          {/* Blockiert der Browser, sagt es schon die Zeile unter dem Schalter. */}
          {active || denied ? null : (
            <SperrGrund ohneSymbol>Erst Desktop-Benachrichtigungen einschalten.</SperrGrund>
          )}
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem
        data-testid="account-menu-push-categories"
        onSelect={(event) => {
          event.preventDefault();
          onOpenPushPrefs();
        }}
      >
        <Smartphone className="size-3.5 text-muted-foreground" />
        {/* NAV-2: heisst wie der Dialog, den er oeffnet — seit PUSH-1 sind
            dort Kategorien UND Zustellprobe, «Push-Kategorien» war zu eng. */}
        <span>Benachrichtigungen aufs Handy…</span>
      </DropdownMenuItem>
    </>
  );
}

// ---------------------------------------------------------------------------
// Mobile (< 768 px) — Sheet-Drawer (Session 35 / J1).
// ---------------------------------------------------------------------------

function MobileTopNav({
  entries,
  isSuperadmin,
}: {
  readonly entries: readonly TopLevelNavEntry[];
  readonly isSuperadmin: boolean;
}) {
  const palette = useCommandPalette();
  const [open, setOpen] = useState(false);
  // G-14: Der Feedback-Flow lebt HIER (nicht im Drawer) — der Drawer
  // unmountet beim Schliessen, der Dialog muss das ueberleben.
  const feedback = useFeedbackFlow();

  function openPaletteFromDrawer() {
    setOpen(false);
    palette.toggle();
  }

  function startFeedbackFromDrawer() {
    setOpen(false);
    void feedback.start({ delayMs: 350 });
  }

  return (
    <>
      <div className="flex items-center gap-1">
        {/* NAV-5: in der Drawer-Shell dieselbe Regel, Touch-Groesse wie der
            Menue-Trigger rechts. */}
        <StandaloneBackButton className="size-10" />
        <HomeLogoLink />
      </div>
      <div className="flex items-center gap-1">
        <NotificationBadgeButton />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-10"
              aria-label="Navigation öffnen"
              data-testid="mobile-nav-trigger"
            >
              <Menu className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full max-w-sm"
            data-testid="mobile-nav-drawer"
          >
            <SheetHeader>
              <SheetTitle>Navigation</SheetTitle>
              <SheetDescription>
                Wechsle zwischen Bereichen und öffne Konto-Einstellungen.
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 py-5">
              <MobileNavSection
                entries={entries}
                onNavigate={() => setOpen(false)}
                isSuperadmin={isSuperadmin}
              />
              <MobileQuickActions
                onOpenPalette={openPaletteFromDrawer}
                feedbackCapturing={feedback.capturing}
                onStartFeedback={startFeedbackFromDrawer}
              />
              <MobileThemeSection />
              <MobileNotificationSection />
              <MobileAccountSection onSignOutClick={() => setOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>
      </div>
      {feedback.dialogNode}
    </>
  );
}

// Ein Link im Drawer — dieselbe Optik fuer Blaetter, Gruppen-Mitglieder,
// Hilfe und System. Vor NAV-1 stand dieser Klassen-Block fuenfmal kopiert.
function MobileNavLink({
  to,
  end = false,
  label,
  icon: Icon,
  disabled = false,
  onNavigate,
  testId,
}: {
  readonly to: string;
  readonly end?: boolean;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly disabled?: boolean;
  readonly onNavigate: () => void;
  readonly testId?: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "inline-flex min-h-11 items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-colors",
          disabled && "pointer-events-none text-muted-foreground/60",
          !disabled &&
            !isActive &&
            "text-muted-foreground hover:bg-surface hover:text-foreground",
          !disabled && isActive && "bg-surface text-foreground",
        )
      }
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
    >
      <Icon className="size-4" />
      {label}
      {to === "/leads" ? <LeadsNavZahl /> : null}
    </NavLink>
  );
}

function MobileNavLeaf({
  item,
  onNavigate,
}: {
  readonly item: NavItemDefinition;
  readonly onNavigate: () => void;
}) {
  return (
    <MobileNavLink
      to={item.to}
      end={item.end}
      label={item.label}
      icon={ICONS_BY_NAV_ID[item.id]}
      disabled={item.disabled}
      onNavigate={onNavigate}
    />
  );
}

// NAV-1: der Drawer rendert dieselbe Struktur wie die Desktop-Leiste — ein
// Blatt als Link, eine zugeklappte Gruppe als Abschnitt mit Ueberschrift und
// ihren Mitgliedern. Flache Rollen (Marvin, Jacqueline) sehen keine
// Abschnitte. Danach Hilfe (alle Rollen) und fuer den Superadmin «System».
function MobileNavSection({
  entries,
  onNavigate,
  isSuperadmin,
}: {
  readonly entries: readonly TopLevelNavEntry[];
  readonly onNavigate: () => void;
  readonly isSuperadmin: boolean;
}) {
  return (
    <nav aria-label="Hauptnavigation" className="flex flex-col gap-1">
      <span className="text-2xs font-mono uppercase tracking-widest text-muted-foreground">
        Bereiche
      </span>
      {entries.map((entry) =>
        entry.kind === "leaf" ? (
          <MobileNavLeaf
            key={entry.item.id}
            item={entry.item}
            onNavigate={onNavigate}
          />
        ) : (
          <MobileNavGroup
            key={entry.group.id}
            group={entry.group}
            items={entry.items}
            onNavigate={onNavigate}
          />
        ),
      )}
      {/* MCP-1: wie im Desktop-Konto-Menue. */}
      <MobileNavLink
        to="/assistent"
        label="Persönlicher Assistent"
        icon={Bot}
        onNavigate={onNavigate}
        testId="mobile-nav-assistent"
      />
      {/* H-4: Hilfe fuer alle Rollen (kein Guard). */}
      <MobileNavLink
        to="/hilfe"
        label="Hilfe"
        icon={HelpCircle}
        onNavigate={onNavigate}
        testId="mobile-nav-help"
      />
      {/* NEU-1: dieselbe Liste wie im Desktop-Konto-Menue. */}
      <MobileNavLink
        to="/was-ist-neu"
        label="Was ist neu"
        icon={Sparkles}
        onNavigate={onNavigate}
        testId="mobile-nav-whats-new"
      />
      {isSuperadmin ? (
        // Superadmin-Pfad zu /system am Handy — auf Desktop lebt der Link im
        // Konto-Menue; der Drawer hatte ihn verloren (E2E-Altlast 2026-07-07),
        // womit Daniel /system mobil gar nicht erreichte.
        <MobileNavLink
          to="/system"
          label="System"
          icon={Settings}
          onNavigate={onNavigate}
        />
      ) : null}
    </nav>
  );
}

// Zugeklappte Gruppe im Drawer: Ueberschrift mit Gruppen-Icon, darunter die
// Mitglieder — dieselben Ziele wie das Desktop-Dropdown.
function MobileNavGroup({
  group,
  items,
  onNavigate,
}: {
  readonly group: NavGroupDefinition;
  readonly items: readonly NavItemDefinition[];
  readonly onNavigate: () => void;
}) {
  const GroupIcon = ICONS_BY_GROUP_ID[group.id];
  return (
    <div
      className="mt-2 flex flex-col gap-1"
      data-testid={`mobilenav-group-${group.id}`}
    >
      <span className="flex items-center gap-2 px-3 text-2xs font-mono uppercase tracking-widest text-muted-foreground">
        <GroupIcon className="size-3.5" />
        {group.label}
      </span>
      {items.map((item) => (
        <MobileNavLeaf key={item.id} item={item} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function MobileQuickActions({
  onOpenPalette,
  feedbackCapturing,
  onStartFeedback,
}: {
  readonly onOpenPalette: () => void;
  readonly feedbackCapturing: boolean;
  readonly onStartFeedback: () => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-2xs font-mono uppercase tracking-widest text-muted-foreground">
        Aktionen
      </span>
      <button
        type="button"
        onClick={onOpenPalette}
        className="inline-flex min-h-11 items-center gap-3 rounded-sm border border-border bg-surface px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground"
      >
        <Search className="size-4" />
        <span>Suchen oder Aktion …</span>
        <span className="ml-auto font-mono text-2xs tracking-widest">⌘K</span>
      </button>
      <FeedbackMenuButton
        capturing={feedbackCapturing}
        onStart={onStartFeedback}
      />
    </div>
  );
}

function MobileThemeSection() {
  const theme = useTheme();
  return (
    <div className="flex flex-col gap-2">
      <span className="text-2xs font-mono uppercase tracking-widest text-muted-foreground">
        Darstellung
      </span>
      <div
        role="radiogroup"
        aria-label="Darstellung"
        className="flex flex-col gap-1"
      >
        {THEME_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isActive = theme === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              data-theme-choice={option.value}
              onClick={() => setTheme(option.value)}
              className={cn(
                "inline-flex min-h-11 items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-surface text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )}
            >
              <Icon className="size-4 text-muted-foreground" />
              <span>{option.label}</span>
              {isActive ? (
                <Check className="ml-auto size-4 text-foreground" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Phase N / NB-1 — mobiler Spiegel von DesktopNotificationMenuItems.
function MobileNotificationSection() {
  const { supported, permission, active, toggle, sendTest } =
    useDesktopNotificationToggle();
  if (!supported) return null;
  const denied = permission === "denied";
  return (
    <div className="flex flex-col gap-2">
      <span className="text-2xs font-mono uppercase tracking-widest text-muted-foreground">
        Benachrichtigungen
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={active}
        disabled={denied}
        data-testid="mobile-desktop-notifications-toggle"
        onClick={() => void toggle()}
        className={cn(
          "inline-flex min-h-11 items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors",
          denied && "pointer-events-none text-muted-foreground/60",
          !denied && active && "bg-surface text-foreground",
          !denied &&
            !active &&
            "text-muted-foreground hover:bg-surface hover:text-foreground",
        )}
      >
        {active ? (
          <BellRing className="size-4 text-muted-foreground" />
        ) : (
          <BellOff className="size-4 text-muted-foreground" />
        )}
        <span>Desktop-Benachrichtigungen</span>
        {active ? <Check className="ml-auto size-4 text-foreground" /> : null}
      </button>
      {denied ? (
        <span className="px-3 text-2xs text-muted-foreground">
          Im Browser blockiert — dort wieder erlauben.
        </span>
      ) : (
        <button
          type="button"
          disabled={!active}
          data-testid="mobile-desktop-notifications-test"
          onClick={() => void sendTest()}
          className={cn(
            "inline-flex min-h-11 items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors",
            !active && "pointer-events-none text-muted-foreground/60",
            active && "text-muted-foreground hover:bg-surface hover:text-foreground",
          )}
        >
          <Bell className="size-4 text-muted-foreground" />
          <span>Test-Benachrichtigung</span>
        </button>
      )}
      {!denied && !active ? (
        <SperrGrund ohneSymbol className="px-3">
          Erst Desktop-Benachrichtigungen einschalten.
        </SperrGrund>
      ) : null}
      <div className="mt-2 border-t border-border pt-3">
        <span className="mb-2 flex items-center gap-2 text-2xs font-mono uppercase tracking-widest text-muted-foreground">
          <Smartphone className="size-3.5" />
          Benachrichtigungen aufs Handy
        </span>
        <Suspense fallback={null}>
          <PushCategoryControls />
        </Suspense>
      </div>
    </div>
  );
}

function MobileAccountSection({
  onSignOutClick,
}: {
  readonly onSignOutClick: () => void;
}) {
  const { user, signOut } = useAuth();
  return (
    <div className="flex flex-col gap-2">
      <span className="text-2xs font-mono uppercase tracking-widest text-muted-foreground">
        Konto
      </span>
      <div className="rounded-sm border border-border bg-surface px-3 py-2">
        <div className="text-sm font-medium text-foreground">{user?.fullName}</div>
        <div className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">
          {user ? USER_ROLE_LABELS[user.role] : ""}
        </div>
        <div className="font-mono text-2xs text-muted-foreground">{user?.email}</div>
      </div>
      <Button
        variant="ghost"
        className="justify-start"
        onClick={() => {
          onSignOutClick();
          void signOut();
        }}
      >
        Abmelden
      </Button>
    </div>
  );
}

